import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Tooltip,
  IconButton,
  FormControlLabel,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useDispatchWithBackdrop } from 'hooks/useDispatchWithBackdrop';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AlertDialogSlide from '../../components/Dialog/AlertDialogSlide';
import DropdownMenu from '../../components/DropdownMenu';
import MDBox from '../../components/MDBox';
import MDInput from '../../components/MDInput';
import MDTypography from '../../components/MDTypography';
import DashboardNavbar from '../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../LayoutContainers/DashboardLayout';
import {
  fetchUserBuildingsDetails,
  createBuildingData,
  updateBuildingData,
  deleteBuildingData,
  createAreaData,
  updateAreaData,
  deleteAreaData,
  fetchUserDevices,
  setDevicesList,
} from '../../stores/sensor/sensorStore';
import { brandColors } from 'types/colors';
import { getOriginalFetch } from '../../utils/routeBasedFetchPatch';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { fetchUserBuildingDetails } from '../../stores/sensor/usersSlice';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import Toggle from '../ControllerPage/components/Toggle';
import MarqueeText from 'components/DevicesFilter/MarqueeText';
import { fontWeight } from '@mui/system';
import {
  createHvacEquipment,
  deleteHvacEquipment,
  getHvacEquipment,
  updateHvacEquipment,
} from '../../services/hvacEquipmentService';
import { updateAreaDevices } from '../../services/buildingService';
import { matchesDeviceSearch } from '../../helpers/filterHelpers';

// Helper functions for filtering
const originalFetch = window.fetch;
const apiUrl = process.env.REACT_APP_BACKEND_URL;
const CAV_WEEK_DAYS = [
  { key: '0', label: 'Sunday' },
  { key: '1', label: 'Monday' },
  { key: '2', label: 'Tuesday' },
  { key: '3', label: 'Wednesday' },
  { key: '4', label: 'Thursday' },
  { key: '5', label: 'Friday' },
  { key: '6', label: 'Saturday' },
];
const DEFAULT_CAV_START_TIME = '07:00';
const DEFAULT_CAV_END_TIME = '22:00';
// Values used whenever the Add HVAC Equipment dialog opens.
const emptyHvacEquipmentForm = { name: '', equipment_type: '', rated_power_kw: '', controller_ids: [], device_ids: [] };
// Keep the type dropdown aligned with the API and database validation.
const HVAC_EQUIPMENT_TYPES = ['AHU-Cooling', 'FAHU', 'FCU-Cooling', 'Chiller'];

const normalizeTimeString = value => {
  if (value === null || value === undefined || value === '') return '';
  const valueString = String(value).trim();
  const hhmm = DateTime.fromFormat(valueString, 'HH:mm');
  if (hhmm.isValid) return hhmm.toFormat('HH:mm');
  const hhmmss = DateTime.fromFormat(valueString, 'HH:mm:ss');
  if (hhmmss.isValid) return hhmmss.toFormat('HH:mm');
  return '';
};

const parseCavSchedule = value => {
  const emptySchedule = CAV_WEEK_DAYS.reduce((acc, day) => ({ ...acc, [day.key]: '' }), {});
  if (value === null || value === undefined || value === '') return emptySchedule;

  let parsedValue = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return emptySchedule;
    try {
      parsedValue = JSON.parse(trimmed);
    } catch {
      const singleTime = normalizeTimeString(trimmed);
      if (!singleTime) return emptySchedule;
      return CAV_WEEK_DAYS.reduce((acc, day) => ({ ...acc, [day.key]: singleTime }), {});
    }
  }

  if (parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)) {
    const schedule = { ...emptySchedule };
    CAV_WEEK_DAYS.forEach(day => {
      schedule[day.key] = normalizeTimeString(parsedValue[day.key]);
    });
    return schedule;
  }

  return emptySchedule;
};

const getCavScheduleFormDefaults = (fieldPrefix, value) => {
  const schedule = parseCavSchedule(value);
  return CAV_WEEK_DAYS.reduce((acc, day) => ({ ...acc, [`${fieldPrefix}_${day.key}`]: schedule[day.key] }), {});
};

const getCavDayEnabledBySchedule = (startValue, endValue) => {
  const startSchedule = parseCavSchedule(startValue);
  const endSchedule = parseCavSchedule(endValue);
  return CAV_WEEK_DAYS.reduce(
    (acc, day) => ({
      ...acc,
      [day.key]: Boolean(startSchedule[day.key] || endSchedule[day.key]),
    }),
    {},
  );
};

const getCavDayEnabledFormDefaults = (startValue, endValue) => {
  const dayEnabledBySchedule = getCavDayEnabledBySchedule(startValue, endValue);
  return CAV_WEEK_DAYS.reduce((acc, day) => ({ ...acc, [`cav_day_enabled_${day.key}`]: dayEnabledBySchedule[day.key] }), {});
};

const isCavActive24Hours = (startValue, endValue) => {
  const startSchedule = parseCavSchedule(startValue);
  const endSchedule = parseCavSchedule(endValue);
  return CAV_WEEK_DAYS.every(day => startSchedule[day.key] === '00:00' && endSchedule[day.key] === '00:00');
};

const serializeCavSchedule = scheduleByDay => {
  return CAV_WEEK_DAYS.reduce((acc, day) => ({ ...acc, [day.key]: normalizeTimeString(scheduleByDay?.[day.key]) }), {});
};

const getCavDayEnabledFromForm = data => {
  return CAV_WEEK_DAYS.reduce((acc, day) => ({ ...acc, [day.key]: Boolean(data?.[`cav_day_enabled_${day.key}`]) }), {});
};

const getScheduleByDayFromForm = (data, fieldPrefix, dayEnabledByForm = null) => {
  return CAV_WEEK_DAYS.reduce(
    (acc, day) => ({
      ...acc,
      [day.key]: dayEnabledByForm && !dayEnabledByForm[day.key] ? '' : data?.[`${fieldPrefix}_${day.key}`] || '',
    }),
    {},
  );
};

// This stays in BuildingForm because it only belongs to Building Management.
function HvacEquipmentSection({ buildingId }) {
  const dispatch = useDispatch();
  // The device list is used to choose the equipment controller and linked devices.
  const allDevices = useSelector(state => state.sensors.devices || []);
  // Equipment list and Add/Edit dialog state.
  const [equipment, setEquipment] = useState([]);
  const [form, setForm] = useState(emptyHvacEquipmentForm);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // A selected target opens the delete confirmation dialog.
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Equipment can only use devices and controllers from its own building.
  const buildingDevices = useMemo(
    () => allDevices.filter(device => String(device.building_id) === String(buildingId)),
    [allDevices, buildingId],
  );
  const controllers = useMemo(
    () => buildingDevices.filter(device => String(device.device_type || '').startsWith('Controller-')),
    [buildingDevices],
  );

  const refresh = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      // The equipment list fills this section. The full device list fills both selectors.
      const [items] = await Promise.all([
        getHvacEquipment(buildingId),
        dispatch(fetchUserDevices({ devicesType: 'all' })).unwrap(),
      ]);
      setEquipment(items);
    } catch (requestError) {
      setError(requestError.message || 'Could not load HVAC equipment.');
    } finally {
      setLoading(false);
    }
  }, [buildingId, dispatch]);

  useEffect(() => {
    // Reload when the parent switches to another building.
    refresh();
  }, [refresh]);

  const openCreate = () => {
    // Clear an earlier edit before opening a fresh equipment form.
    setEditingId(null);
    setForm(emptyHvacEquipmentForm);
    setError('');
    setOpen(true);
  };

  const openEdit = item => {
    // Keep the equipment ID so Save uses PUT instead of POST.
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      equipment_type: item.equipment_type || '',
      rated_power_kw: item.rated_power_kw ?? '',
      controller_ids: Array.isArray(item.controller_ids) ? item.controller_ids.map(String) : [],
      // The API returns full device objects; the form only needs their IDs.
      device_ids: (item.devices || []).map(device => String(device.id)),
    });
    setError('');
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.equipment_type.trim()) {
      setError('Name and type are required.');
      return;
    }
    setLoading(true);
    setError('');
    // Send numbers to the API even though HTML select values are strings.
    const payload = {
      building_id: Number(buildingId),
      name: form.name.trim(),
      equipment_type: form.equipment_type.trim(),
      // Match area_size: an empty numeric field is stored as NULL.
      rated_power_kw: form.rated_power_kw || null,
      controller_ids: form.controller_ids.map(Number),
      device_ids: form.device_ids.map(Number),
    };
    try {
      // Create has no ID yet; editing uses the ID of the selected equipment.
      if (editingId) await updateHvacEquipment(editingId, payload);
      else await createHvacEquipment(payload);
      setOpen(false);
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Could not save HVAC equipment.');
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    setError('');
    try {
      // The database trigger cleans the deleted ID from every linked device.
      await deleteHvacEquipment(deleteTarget.id);
      setDeleteTarget(null);
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Could not delete HVAC equipment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title="HVAC equipment"
          action={
            <Button size="small" disabled={loading} onClick={openCreate}>
              + Add HVAC equipment
            </Button>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!loading && equipment.length === 0 && <Typography>No HVAC equipment has been added yet.</Typography>}
          <Stack spacing={1.5}>
            {/* Each card shows one equipment record and its manually linked devices. */}
            {equipment.map(item => (
              <Box key={item.id} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography fontWeight={700}>{item.name}</Typography>
                    <Typography variant="body2">
                      {item.equipment_type} · Controllers:{' '}
                      {(item.controllers || []).length ? item.controllers.map(controller => controller.name).join(', ') : 'None'}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(item.devices || []).map(device => (
                        <Chip key={device.id} size="small" label={device.name} />
                      ))}
                    </Box>
                  </Box>
                  <Stack direction="row" alignSelf="flex-start">
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(item)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteTarget(item)}>
                      Delete
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* One dialog is shared by both Add and Edit modes. */}
      <Dialog open={open} onClose={() => !loading && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit HVAC equipment' : 'Add HVAC equipment'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Box sx={{ flex: 4.25 }}>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={event => setForm({ ...form, name: event.target.value })}
                  required
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: 4.25 }}>
                <FormControl fullWidth required>
                  <InputLabel id="hvac-type-label">Type</InputLabel>
                  <Select
                    labelId="hvac-type-label"
                    label="Type"
                    value={form.equipment_type}
                    onChange={event => setForm({ ...form, equipment_type: event.target.value })}
                    MenuProps={{ PaperProps: { sx: { mt: '-5px' } } }}>
                    {HVAC_EQUIPMENT_TYPES.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 3.5 }}>
                <TextField
                  label="Rated Power (kW)"
                  value={form.rated_power_kw}
                  onChange={event => setForm({ ...form, rated_power_kw: event.target.value })}
                  fullWidth
                  type="number"
                />
              </Box>
            </Box>
            <FormControl fullWidth>
              {/* Controllers are optional, may be selected together, and are not linked devices automatically. */}
              <InputLabel id="hvac-controllers-label">Controllers</InputLabel>
              <Select
                labelId="hvac-controllers-label"
                label="Controllers"
                multiple
                value={form.controller_ids}
                onChange={event => setForm({ ...form, controller_ids: event.target.value.map(String) })}
                input={<OutlinedInput label="Controllers" />}
                MenuProps={{ PaperProps: { sx: { mt: '-5px' } } }}
                renderValue={selected => (selected.length ? `${selected.length} selected` : 'No controllers')}>
                {controllers.map(device => (
                  <MenuItem
                    key={device.id}
                    value={String(device.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      minHeight: 38,
                      py: 0,
                      fontSize: '1rem',
                    }}>
                    <Checkbox
                      checked={form.controller_ids.includes(String(device.id))}
                      sx={{ width: 32, height: 32, p: 0.5, mr: 0.5 }}
                    />
                    <ListItemText
                      primary={`${device.name} (${device.device_type})`}
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="hvac-sensors-label">Sensors</InputLabel>
              <Select
                labelId="hvac-sensors-label"
                label="Sensors"
                multiple
                value={form.device_ids}
                onChange={event => setForm({ ...form, device_ids: event.target.value.map(String) })}
                input={<OutlinedInput label="Sensors" />}
                sx={{ fontSize: '1rem' }}
                MenuProps={{
                  disableScrollLock: true,
                  variant: 'menu',
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  PaperProps: { sx: { maxHeight: '40vh', mt: '-5px' } },
                  MenuListProps: { style: { paddingTop: '0px', paddingBottom: '0px', paddingLeft: '1px', width: 'calc(100%)' } },
                  TransitionProps: {
                    onEntered: element => {
                      const list = element.querySelector('[role="listbox"]');
                      if (list) list.scrollTop = 0;
                    },
                  },
                }}
                renderValue={selected =>
                  selected.length ? `${selected.length} linked device${selected.length === 1 ? '' : 's'}` : ''
                }>
                {buildingDevices.map(device => (
                  <MenuItem
                    key={device.id}
                    value={String(device.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      minHeight: 38,
                      py: 0,
                      fontSize: '1rem',
                    }}>
                    <Checkbox
                      checked={form.device_ids.includes(String(device.id))}
                      sx={{ width: 32, height: 32, p: 0.5, mr: 0.5 }}
                    />
                    <ListItemText primary={device.name} primaryTypographyProps={{ fontSize: '1rem' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button disabled={loading} variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialogSlide
        open={Boolean(deleteTarget)}
        title="Confirm HVAC Equipment Deletion"
        content={
          <Box sx={{ padding: '8px', fontWeight: 'bold', width: '500px' }}>
            Delete HVAC equipment "{deleteTarget?.name}"?
            <br />
            <br />
            Its linked devices will remain in the building, but will no longer belong to this HVAC equipment.
            <br />
            <br />
            This action cannot be undone.
          </Box>
        }
        handleClose={() => !loading && setDeleteTarget(null)}
        handleOk={remove}
      />
    </>
  );
}

// Show one Devices dropdown in both area screens.
function DevicesSelect({ id, devices, selectedDeviceIds, setSelectedDeviceIds, onClose, buildingName }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState({ top: 0, left: 0 });
  const [menuHasScrollbar, setMenuHasScrollbar] = useState(false);
  const [menuHeight, setMenuHeight] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState('');
  const selectContainerRef = useRef(null);
  const menuPaperRef = useRef(null);
  const matchingDevices = devices.filter(device => matchesDeviceSearch(device, deviceSearch));

  useEffect(() => {
    if (!menuOpen) {
      setMenuHasScrollbar(false);
      setMenuHeight(null);
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      const menu = menuPaperRef.current;
      const hasScrollbar = Boolean(menu && menu.scrollHeight > menu.clientHeight);
      setMenuHasScrollbar(hasScrollbar);
      setMenuHeight(hasScrollbar ? menu.clientHeight : null);
    });
    return () => cancelAnimationFrame(frame);
  }, [menuOpen]);

  return (
    <FormControl ref={selectContainerRef} sx={{ width: 500, maxWidth: '100%', position: 'relative' }}>
      <Tooltip
        title={buildingName ? `Only devices from ${buildingName} are available.` : 'Only devices from this building are available.'}
        placement="top"
        arrow>
        <InputLabel id={`area-devices-${id}`} shrink sx={{ pointerEvents: 'auto' }}>
          Devices
          <HelpIcon sx={{ fontSize: 14, ml: 0.25, verticalAlign: '-0.15em' }} />
        </InputLabel>
      </Tooltip>
      <Select
        labelId={`area-devices-${id}`}
        label="Devices (?)"
        multiple
        displayEmpty
        open={menuOpen}
        onOpen={() => {
          const field = selectContainerRef.current;
          setMenuWidth(field?.clientWidth ?? null);
          setMenuAnchorPosition({ top: field?.clientHeight ?? 0, left: 0 });
          setMenuOpen(true);
        }}
        onClose={() => {
          setMenuOpen(false);
          setDeviceSearch('');
          onClose();
        }}
        value={selectedDeviceIds}
        onChange={event => {
          const nextDeviceIds = event.target.value.map(String);
          setSelectedDeviceIds(nextDeviceIds);
        }}
        input={<OutlinedInput label="Devices (?)" />}
        sx={{ fontSize: '1rem' }}
        MenuProps={{
          disableScrollLock: true,
          disablePortal: true,
          variant: 'menu',
          anchorReference: 'anchorPosition',
          anchorPosition: menuAnchorPosition,
          marginThreshold: null,
          slotProps: { root: { sx: { position: 'absolute' } } },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: {
            ref: menuPaperRef,
            sx: {
              width: menuWidth,
              maxWidth: menuWidth,
              maxHeight: '40vh',
              height: menuHeight,
              mt: '-5px',
              pt: '0 !important',
              pb: '8px !important',
              overflowX: 'hidden',
              scrollbarGutter: 'auto',
            },
          },
          MenuListProps: { sx: { p: '0 !important', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' } },
          TransitionProps: {
            onEntered: element => {
              const list = element.querySelector('[role="listbox"]');
              if (list) {
                list.scrollTop = 0;
                list.scrollLeft = 0;
              }
            },
          },
        }}
        renderValue={selected => (selected.length ? `${selected.length} linked device${selected.length === 1 ? '' : 's'}` : '')}>
        <ListSubheader
          disableGutters
          sx={{
            display: 'block',
            minHeight: 0,
            m: 0,
            p: 0,
            mr: menuHasScrollbar ? 1.5 : 0,
            mb: 0.5,
            lineHeight: 0,
            bgcolor: 'transparent',
            zIndex: 1,
          }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            placeholder="Search devices"
            value={deviceSearch}
            onChange={event => setDeviceSearch(event.target.value)}
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}
            InputProps={{ disableUnderline: true }}
            sx={{
              bgcolor: '#f1f3f7',
              borderRadius: 1,
              display: 'block',
              '& .MuiInputBase-input': { px: 1, py: 0.75, fontSize: '0.9rem' },
            }}
          />
        </ListSubheader>
        {matchingDevices.map(device => (
          <MenuItem
            key={device.id}
            value={String(device.id)}
            disableGutters
            sx={{
              '&.MuiMenuItem-root': {
                height: '38px',
                minHeight: '38px !important',
                maxHeight: '38px',
                py: 0,
                px: 0,
                mr: menuHasScrollbar ? 1.5 : 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}>
            <Checkbox checked={selectedDeviceIds.includes(String(device.id))} sx={{ width: 32, height: 32, p: 0.5, mr: 0.5 }} />
            <ListItemText
              sx={{ minWidth: 0, height: '100%', my: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}
              primaryTypographyProps={{ component: 'div', sx: { display: 'flex', alignItems: 'center', width: '100%' } }}
              primary={<MarqueeText animationDuration="7s">{device.name}</MarqueeText>}
            />
          </MenuItem>
        ))}
        {matchingDevices.length === 0 && <MenuItem disabled>No devices found</MenuItem>}
      </Select>
    </FormControl>
  );
}

function FloorSelect({ id, value, onChange, floors }) {
  const [menuWidth, setMenuWidth] = useState(null);
  const selectContainerRef = useRef(null);

  return (
    <FormControl ref={selectContainerRef} fullWidth>
      <InputLabel id={`${id}-label`}>Floor</InputLabel>
      <Select
        labelId={`${id}-label`}
        value={value ?? ''}
        label="Floor"
        onChange={onChange}
        onOpen={() => setMenuWidth(selectContainerRef.current?.clientWidth ?? null)}
        input={<OutlinedInput label="Floor" />}
        MenuProps={{
          disableScrollLock: true,
          variant: 'menu',
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: { sx: { width: menuWidth, maxWidth: menuWidth, maxHeight: '40vh', mt: '-5px' } },
          MenuListProps: { sx: { py: 0 } },
        }}>
        {floors.map(floorObj => (
          <MenuItem key={floorObj.id} value={String(floorObj.floor)}>
            {floorObj.floor}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Choose the devices for this area. Update saves changes.
function AreaDevicesSelector({ area, buildingId, buildingName, allDevices, selectedDeviceIds, setSelectedDeviceIds }) {
  // Keep this list stable until the building or device list changes.
  const devices = useMemo(
    () => allDevices.filter(device => String(device.building_id) === String(buildingId)),
    [allDevices, buildingId],
  );

  const savedDeviceIds = useMemo(
    () =>
      devices
        .filter(device => String(device.area_id) === String(area.id))
        .map(device => String(device.id))
        .sort(),
    [area.id, devices],
  );
  const selectedIds = selectedDeviceIds ?? savedDeviceIds;

  return (
    <Box sx={{ width: '100%' }}>
      <DevicesSelect
        id={area.id}
        devices={devices}
        selectedDeviceIds={selectedIds}
        setSelectedDeviceIds={setSelectedDeviceIds}
        onClose={() => {}}
        buildingName={buildingName}
      />
      {devices.length === 0 && <Typography variant="caption">No devices are available in this building.</Typography>}
    </Box>
  );
}

const BuildingForm = () => {
  const currentUser = useSelector(state => state.sensors.currentUser);
  const { dispatchWithBackdrop } = useDispatchWithBackdrop();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  //local states
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [addFloorMode, setAddFloorMode] = useState(false);
  const [addAreaMode, setAddAreaMode] = useState(false);
  // Keep devices chosen before a new area exists.
  const [newAreaDeviceIds, setNewAreaDeviceIds] = useState([]);
  // Keep unsaved device choices for each existing area.
  const [areaDeviceIds, setAreaDeviceIds] = useState({});

  // Get device ID from URL params
  let { buildingId } = useParams();
  if (buildingId === 'new') {
    buildingId = 0;
  }

  const isAddMode = buildingId === 'new' || buildingId === 0;

  // State management
  const [uploading, setUploading] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteFloorModalOpen, setDeleteFloorModalOpen] = useState(false);
  const [deleteAreaModalOpen, setDeleteAreaModalOpen] = useState(false);
  const [deletingFloor, setDeletingFloor] = useState(null);
  const [deletingArea, setDeletingArea] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [isBuildingLoading, setIsBuildingLoading] = useState(!isAddMode);
  const [expandedFloors, setExpandedFloors] = useState({});
  const [updatingAreaId, setUpdatingAreaId] = useState(null);
  // Redux selectors
  const buildings = useSelector(state => state.sensors.buildings_details);
  const allDevices = useSelector(state => state.sensors.devices || []);
  const loading = useSelector(state => state.sensors.loading);

  // List only devices from the building for a new area.
  const newAreaDevices = useMemo(
    () => allDevices.filter(device => String(device.building_id) === String(selectedBuilding?.id)),
    [allDevices, selectedBuilding?.id],
  );

  // Building Form
  const {
    handleSubmit: handleBuildingSubmit,
    reset: resetEdit,
    control: controlEdit,
    watch: watchEdit,
    setValue: setValueEdit,
    formState: { errors: errorsEdit, isDirty: isBuildingDirty },
  } = useForm({
    defaultValues: {
      buildingId: 0,
      name: '',
      country: '',
      city: '',
      street: '',
      zipCode: '',
      ...getCavScheduleFormDefaults('cav_start_time', DEFAULT_CAV_START_TIME),
      ...getCavScheduleFormDefaults('cav_end_time', DEFAULT_CAV_END_TIME),
      ...getCavDayEnabledFormDefaults(DEFAULT_CAV_START_TIME, DEFAULT_CAV_END_TIME),
      cav_active_24_7: false,
    },
  });

  // Floor Form
  const {
    handleSubmit: handleFloorSubmit,
    reset: resetFloor,
    control: controlFloor,
    watch: watchFloor,
    setValue: setValueFloor,
    formState: { errors: errorsFloor },
  } = useForm({
    defaultValues: {},
  });

  const newFloorVal = watchFloor('new_floor');
  const isNewFloorMissing =
    addFloorMode &&
    (newFloorVal === '' || newFloorVal === undefined || newFloorVal === null || Number.isNaN(Number(newFloorVal))); // treats 0 as valid

  // Area Form
  const {
    handleSubmit: handleAreaSubmit,
    reset: resetArea,
    control: controlArea,
    watch: watchArea,
    setValue: setValueArea,
    formState: { errors: errorsArea },
  } = useForm({
    defaultValues: {},
  });

  // compute "new area" disabled state
  const newAreaName = (watchArea('new_name') ?? '').trim();
  const isNewAreaMissing = addAreaMode && newAreaName.length === 0;

  useEffect(() => {
    let isMounted = true;

    if (!buildingId || isAddMode) {
      setSelectedBuilding(null);
      setIsBuildingLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (buildings?.length) {
      const building = buildings.find(b => b.id.toString() === buildingId.toString()) || null;
      setSelectedBuilding(building);
      setIsBuildingLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setIsBuildingLoading(true);
    dispatch(fetchUserBuildingDetails({ buildingId }))
      .unwrap()
      .then(res => {
        if (isMounted) setSelectedBuilding(res || null);
      })
      .catch(error => {
        if (isMounted && error?.statusCode === 403) {
          navigate('/unauthorized', { replace: true });
        }
      })
      .finally(() => {
        if (isMounted) setIsBuildingLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [buildingId, buildings, dispatch, isAddMode, navigate]);

  useEffect(() => {
    if (!isAddMode && selectedBuilding?.id) {
      dispatch(fetchUserDevices({ devicesType: 'all' }));
    }
  }, [dispatch, isAddMode, selectedBuilding?.id]);

  // Update Redux in memory after a device-only save, so we do not call devices/all again.
  const applyAreaDeviceChanges = (areaId, addedDeviceIds, removedDeviceIds) => {
    const addedIds = new Set(addedDeviceIds.map(String));
    const removedIds = new Set(removedDeviceIds.map(String));

    dispatch(
      setDevicesList(
        allDevices.map(device => {
          // An unassigned device must disappear from this building page.
          if (removedIds.has(String(device.id))) {
            return { ...device, area_id: null, building_id: null };
          }
          if (addedIds.has(String(device.id))) {
            return { ...device, area_id: Number(areaId) };
          }
          return device;
        }),
      ),
    );
  };

  // Helper function to handle empty/null values
  const getValueOrDefault = value => {
    return value === null || value === undefined || value === '' ? '' : value;
  };

  const getDisplayValue = value => {
    return value === null || value === undefined || value === '' ? '-Unassigned-' : value;
  };

  const toLuxonTime = value => {
    const normalized = normalizeTimeString(value);
    if (!normalized) return null;
    return DateTime.fromFormat(normalized, 'HH:mm');
  };

  // Initialize data on component mount
  useEffect(() => {
    if (selectedBuilding && !isAddMode) {
      resetEdit({
        name: getValueOrDefault(selectedBuilding.name),
        country: getValueOrDefault(selectedBuilding.country),
        city: getValueOrDefault(selectedBuilding.city),
        street: getValueOrDefault(selectedBuilding.street),
        zipCode: getValueOrDefault(selectedBuilding.zipCode),
        ...getCavScheduleFormDefaults('cav_start_time', selectedBuilding.cav_start_time),
        ...getCavScheduleFormDefaults('cav_end_time', selectedBuilding.cav_end_time),
        ...getCavDayEnabledFormDefaults(selectedBuilding.cav_start_time, selectedBuilding.cav_end_time),
        cav_active_24_7: isCavActive24Hours(selectedBuilding.cav_start_time, selectedBuilding.cav_end_time),
      });

      // Initialize floor forms
      if (selectedBuilding.floors) {
        Object.values(selectedBuilding.floors).forEach(floor => {
          setValueFloor(`floor_${floor.id}`, floor.floor);
          setValueFloor(`area_size_${floor.id}`, floor.area_size);
        });
      }

      // Initialize area forms
      if (selectedBuilding.areas) {
        Object.values(selectedBuilding.areas).forEach(area => {
          setValueArea(`name_${area.id}`, area.name);
          setValueArea(`floor_${area.id}`, area.floor);
          setValueArea(`area_size_${area.id}`, area.area_size);
        });
      }
    }
  }, [selectedBuilding, resetEdit, setValueFloor, setValueArea, isAddMode]);

  // Auto-fill form when device is selected from URL
  useEffect(() => {
    if (selectedBuilding && isInitialLoad && buildings.length > 0) {
      handleReset();
    }
  }, [selectedBuilding, isInitialLoad]);

  // Handle Building submission
  const onEditSubmitBuilding = async data => {
    try {
      const dayEnabledByForm = getCavDayEnabledFromForm(data);
      const building = {
        buildingId: buildingId,
        name: data.name,
        country: data.country,
        city: data.city,
        street: data.street,
        zipCode: data.zipCode,
        cav_start_time: serializeCavSchedule(getScheduleByDayFromForm(data, 'cav_start_time', dayEnabledByForm)),
        cav_end_time: serializeCavSchedule(getScheduleByDayFromForm(data, 'cav_end_time', dayEnabledByForm)),
      };

      if (isAddMode) {
        await dispatch(createBuildingData(building)).unwrap();
      } else {
        await dispatch(updateBuildingData(building)).unwrap();
      }

      setNotification({
        open: true,
        message: `Building ${isAddMode ? 'created' : 'updated'} successfully!`,
        severity: 'success',
      });

      if (!isAddMode) {
        resetEdit(data);
      }

      // Refresh buildings list
      // dispatch(fetchUserBuildingsDetails());
    } catch (error) {
      console.error('Update failed:', error);
      setNotification({
        open: true,
        message: error.message || 'An unexpected error occurred',
        severity: 'error',
      });
    }
  };

  // Reset form to original device values
  const handleReset = () => {
    if (selectedBuilding) {
      // Reset the flag to allow auto-fill to run again
      setIsInitialLoad(true);

      // Create the original form values object
      const originalValues = {
        name: getValueOrDefault(selectedBuilding.name),
        country: getValueOrDefault(selectedBuilding.country),
        city: getValueOrDefault(selectedBuilding.city),
        street: getValueOrDefault(selectedBuilding.street),
        zipCode: getValueOrDefault(selectedBuilding.zipCode),
        ...getCavScheduleFormDefaults('cav_start_time', selectedBuilding.cav_start_time),
        ...getCavScheduleFormDefaults('cav_end_time', selectedBuilding.cav_end_time),
        ...getCavDayEnabledFormDefaults(selectedBuilding.cav_start_time, selectedBuilding.cav_end_time),
        cav_active_24_7: isCavActive24Hours(selectedBuilding.cav_start_time, selectedBuilding.cav_end_time),
      };

      // Reset the form with original values
      resetEdit(originalValues);
    }
  };

  ////////////////////
  /// HANDLE UPDATE

  const handleFloorUpdate = async (floorData, selectedFile = null) => {
    try {
      setUploading(prev => ({ ...prev, [floorData.floor]: true }));

      // Get current form values for this floor
      const formData = watchFloor();

      if (floorData.floor === '' || floorData.floor === undefined || floorData.floor === null) {
        setNotification({
          open: true,
          message: 'Please select a number to the new floor',
          severity: 'error',
        });
        return;
      }

      // Prepare floor data
      const floorUpdateData = {
        building_id: selectedBuilding.id,
        name: 'floor',
        floor: formData[`floor_${floorData.id}`] || floorData.floor,
        area_size: formData[`floor_size_${floorData.id}`] || floorData.floor_size,
        scheme: floorData.scheme,
        id: floorData.id === 0 ? 0 : floorData.id,
        is_full_floor: true,
      };

      // Upload a new image if provided
      if (selectedFile) {
        const uploadResponse = await fetch(
          `${apiUrl}upload/url?building_name=${selectedBuilding.name}&floor_number=${floorUpdateData.floor}`,
        );
        const { uploadURL, key } = await uploadResponse.json();

        await originalFetch(uploadURL, {
          method: 'PUT',
          body: selectedFile,
        });

        // Update the scheme with the new image key
        floorUpdateData.scheme = key;
      } else {
        // Keep existing scheme if no new file uploaded
        floorUpdateData.scheme = floorData.scheme;
      }

      // Determine if this is a new floor
      const isNewFloor = floorData.id === 0 || !floorData.id;

      // Update / add floor
      await dispatch(updateAreaData(floorUpdateData)).unwrap();
      setNotification({
        open: true,
        message: 'Floor updated successfully!',
        severity: 'success',
      });

      // Refresh building data to get updated floors
      dispatch(fetchUserBuildingsDetails());

      // Clear selected file after successful update
      setSelectedFiles(prev => ({
        ...prev,
        [floorUpdateData.floor]: null,
      }));

      // If this was adding a new floor, turn off add mode
      if (isNewFloor) {
        setAddFloorMode(false);
        setSelectedFiles(prev => ({
          ...prev,
          [0]: null,
        }));
      }

      setUploading(prev => ({ ...prev, [floorData.floor]: false }));
    } catch (error) {
      console.error('Floor update failed:', error);
      setUploading(prev => ({ ...prev, [floorData.floor]: false }));
      setNotification({
        open: true,
        message: error.message || 'Failed to update floor',
        severity: 'error',
      });
    }
  };

  const handleAreaUpdate = async areaData => {
    setUpdatingAreaId(areaData.id);
    try {
      let updatedAreaData;

      if (
        areaData.floor === '' ||
        areaData.floor === null ||
        areaData.floor === undefined ||
        Number.isNaN(Number(areaData.floor))
      ) {
        setNotification({
          open: true,
          message: 'Please select a floor number to the new area',
          severity: 'error',
        });
        return;
      }

      if (areaData.id === 0) {
        // New area - get form values
        const formData = watchArea();
        updatedAreaData = {
          building_id: selectedBuilding.id,
          id: 0,
          name: formData.new_name,
          floor: Number(formData.new_floor),
          area_size: parseFloat(formData.new_area_size) || 0,
        };
      } else {
        // Existing area - get form values
        const formData = watchArea();
        updatedAreaData = {
          building_id: selectedBuilding.id,
          id: areaData.id,
          name: formData[`name_${areaData.id}`] || areaData.name,
          floor: formData[`floor_${areaData.id}`] !== '' ? parseInt(formData[`floor_${areaData.id}`]) : null,
          area_size: parseFloat(formData[`area_size_${areaData.id}`]) || 0,
        };
      }

      const floorsList = Array.isArray(selectedBuilding?.floors)
        ? selectedBuilding.floors
        : Object.values(selectedBuilding?.floors || {});
      const selectedFloor = floorsList.find(floorObj => Number(floorObj.floor) === Number(updatedAreaData.floor));
      updatedAreaData.floor_id = selectedFloor?.id ?? null;
      // Check whether name, floor, or size really changed.
      const areaFieldsChanged =
        areaData.id === 0 ||
        norm(updatedAreaData.name) !== norm(areaData.name) ||
        num(updatedAreaData.floor) !== num(areaData.floor) ||
        num(updatedAreaData.area_size) !== num(areaData.area_size);

      if (areaData.id === 0) {
        // Create the new area before assigning devices to it.
        const result = await dispatch(createAreaData(updatedAreaData)).unwrap();
        if (newAreaDeviceIds.length > 0) {
          await updateAreaDevices(result.areaId, newAreaDeviceIds.map(Number), []);
          applyAreaDeviceChanges(result.areaId, newAreaDeviceIds, []);
        }
      } else {
        // Skip the area request when only devices changed.
        if (areaFieldsChanged) {
          await dispatch(updateAreaData(updatedAreaData)).unwrap();
        }

        const selectedDeviceIds = areaDeviceIds[areaData.id];
        if (selectedDeviceIds) {
          // Read the devices currently saved in this area.
          const savedDeviceIds = allDevices
            .filter(device => String(device.area_id) === String(areaData.id))
            .map(device => String(device.id));
          // Find the devices the user checked and unchecked.
          const addedDeviceIds = selectedDeviceIds.filter(deviceId => !savedDeviceIds.includes(deviceId));
          const removedDeviceIds = savedDeviceIds.filter(deviceId => !selectedDeviceIds.includes(deviceId));

          if (addedDeviceIds.length > 0 || removedDeviceIds.length > 0) {
            await updateAreaDevices(areaData.id, addedDeviceIds.map(Number), removedDeviceIds.map(Number));
            applyAreaDeviceChanges(areaData.id, addedDeviceIds, removedDeviceIds);
          }

          // Clear these saved checkbox changes after they are applied.
          setAreaDeviceIds(previous => {
            const next = { ...previous };
            delete next[areaData.id];
            return next;
          });
        }
      }

      setNotification({
        open: true,
        message:
          areaData.id === 0
            ? `Area '${updatedAreaData.name}' ( Floor: ${updatedAreaData.floor} ) created successfully!`
            : areaFieldsChanged
              ? `Area '${updatedAreaData.name}' ( Floor: ${updatedAreaData.floor} ) updated successfully!`
              : 'Devices updated successfully!',
        severity: 'success',
      });

      // Refresh building data only when the area itself changed.
      if (areaFieldsChanged) {
        dispatch(fetchUserBuildingsDetails());
      }

      // If adding new area, close add mode
      if (areaData.id === 0) {
        setAddAreaMode(false);
        resetArea();
        setNewAreaDeviceIds([]);
      }
    } catch (error) {
      console.error('Area update failed:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to update area',
        severity: 'error',
      });
    } finally {
      setUpdatingAreaId(null);
    }
  };

  const displayImage = async imageName => {
    const response = await fetch(`/api/upload/download-url?key=${imageName}`);
    const { downloadURL } = await response.json();
    return downloadURL; // Use this for <img src={downloadURL} />
  };

  /////////////////
  // HANDLE DELETE
  const handleDeleteFloor = floor => {
    setDeletingFloor(floor);
    setDeleteFloorModalOpen(true);
  };

  const handleDeleteArea = area => {
    setDeletingArea(area);
    setDeleteAreaModalOpen(true);
  };

  const handleDeleteFloorConfirm = async () => {
    try {
      // Add your floor deletion logic here
      await dispatch(deleteAreaData(deletingFloor.id)).unwrap();

      setNotification({
        open: true,
        message: `Floor ${deletingFloor.floor} deleted successfully!`,
        severity: 'success',
      });

      dispatch(fetchUserBuildingsDetails());

      setDeleteFloorModalOpen(false);
      setDeletingFloor(null);
    } catch (error) {
      console.error('Delete floor failed:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to delete floor',
        severity: 'error',
      });
    }
  };
  // handle delete area and floor
  const handleDeleteAreaConfirm = async () => {
    try {
      await dispatch(deleteAreaData(deletingArea.id)).unwrap();

      setNotification({
        open: true,
        message: `Area '${deletingArea.name}' ( Floor: ${deletingArea.floor} ) deleted successfully!`,
        severity: 'success',
      });

      setDeleteAreaModalOpen(false);
      setDeletingArea(null);

      // Refresh building data
      dispatch(fetchUserBuildingsDetails());
    } catch (error) {
      console.error('Delete area failed:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to delete area',
        severity: 'error',
      });
    }
  };

  // Handle Delete Building alert
  const handleDeleteClick = () => {
    if (!buildingId) {
      setNotification({
        open: true,
        message: 'No building selected for deletion.',
        severity: 'error',
      });
      return;
    }
    setDeleteModalOpen(true);
  };

  // building delete
  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteBuildingData(buildingId)).unwrap();

      setNotification({
        open: true,
        message: 'Building deleted successfully! - all devices are now unAssigned',
        severity: 'success',
      });

      setDeleteModalOpen(false);

      // Navigate back to buildings list after deletion
      setTimeout(() => navigate('/manage-buildings'), 1000);
    } catch (error) {
      console.log('🔍 Building deletion error details:');
      console.log('Error object:', error);
      console.log('Error message:', error.message);
      console.log('Error type:', typeof error);
      console.log('Error keys:', Object.keys(error));

      console.error('Delete failed:', error);
      setNotification({
        open: true,
        message: error.message || 'An unexpected error occurred',
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    if (!addAreaMode) return;
    const floors = (selectedBuilding?.floors ?? []).slice().sort((a, b) => a.floor - b.floor);
    const firstFloor = floors[0]?.floor;

    setValueArea('new_name', ''); // init text field
    setValueArea('new_area_size', ''); // init number text field
    setValueArea('new_floor', firstFloor !== undefined ? String(firstFloor) : '');
  }, [addAreaMode, selectedBuilding, setValueArea]);

  // --- Dirty-check helpers ---
  const norm = v => (v === undefined || v === null ? '' : String(v).trim());
  const num = v => (v === '' || v === undefined || v === null ? null : Number(v));

  const buildingEditVals = watchEdit();

  const floorVals = watchFloor();
  const areaVals = watchArea();

  // Some code paths set area_size_*, others floor_size_*; check both safely
  const getFloorSizeVal = id => floorVals?.[`floor_size_${id}`] ?? floorVals?.[`area_size_${id}`];

  const isFloorDirty = floor => {
    const curNum = floorVals?.[`floor_${floor.id}`];
    const curSize = getFloorSizeVal(floor.id);
    const changedNumber = num(curNum) !== num(floor.floor);
    const changedSize = num(curSize) !== num(floor.area_size);
    const hasNewFile = Boolean(selectedFiles[floor.floor]); // enable if a file was chosen
    return changedNumber || changedSize || hasNewFile;
  };

  const isAreaDirty = area => {
    const curName = areaVals?.[`name_${area.id}`];
    const curFloor = areaVals?.[`floor_${area.id}`];
    const curSize = areaVals?.[`area_size_${area.id}`];

    const changedName = norm(curName) !== norm(area.name);
    const changedFloor = num(curFloor) !== num(area.floor);
    const changedSize = num(curSize) !== num(area.area_size);

    return changedName || changedFloor || changedSize;
  };

  // Check whether this area's device choices have changed.
  const isAreaDevicesDirty = area => {
    // Read the choices waiting for this area's Update button.
    const selectedDeviceIds = areaDeviceIds[area.id];
    if (!selectedDeviceIds) return false;

    // Read the device IDs saved in this area.
    const savedDeviceIds = allDevices
      .filter(device => String(device.area_id) === String(area.id))
      .map(device => String(device.id))
      .sort();
    return selectedDeviceIds.slice().sort().join(',') !== savedDeviceIds.join(',');
  };

  const onlyDigits = s => s.replace(/\D/g, '');
  const floorsList = Object.values(selectedBuilding?.floors || {});
  const areasList = Object.values(selectedBuilding?.areas || {});
  const getAreasForFloor = floorNumber => areasList.filter(area => num(area.floor) === num(floorNumber));

  return (
    <DashboardLayout>
      <DashboardNavbar objectName="Edit Building" />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>

      {/* Header */}
      <MDBox
        my={1}
        px={2}
        py={1}
        variant="gradient"
        borderRadius="lg"
        coloredShadow="light"
        style={{
          background: brandColors.BLUE,
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" marginLeft={2} spacing={2} alignItems="end">
            <MDTypography variant="h4" color="light">
              {isAddMode ? 'Add New Building' : `Edit Building: ${selectedBuilding?.name || selectedBuilding?.id}`}
            </MDTypography>
          </Stack>
        </Stack>
      </MDBox>

      {isBuildingLoading ? (
        <Box
          className="form-body"
          sx={{
            margin: {
              xs: '0px 0px',
              md: '0px 10rem',
              lg: '0px 22rem',
              xl: '0px 30rem',
              xxl: '0px 42rem',
            },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '40vh',
          }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          className="form-body"
          sx={{
            margin: {
              xs: '0px 0px',
              md: '0px 10rem',
              lg: '0px 20rem',
              xl: '0px 30rem',
              xxl: '0px 40rem',
            },
          }}>
          <Grid container spacing={3}>
            {/* BUILDING INFO SECTION */}
            {selectedBuilding && !isAddMode && (
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Building Information" sx={{ color: 'white' }} />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Building Name: <strong>{selectedBuilding.name}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Country: <strong>{getDisplayValue(selectedBuilding.country)}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          City: <strong>{getDisplayValue(selectedBuilding.city)}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Street: <strong>{getDisplayValue(selectedBuilding.street)}</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Devices Count: <strong>{selectedBuilding.devices_count || 0}</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* EDIT/ADD BUILDING SECTION */}
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title={isAddMode ? 'Add New Building' : 'Edit Building Details'}
                  sx={{ bgcolor: 'white', color: 'white' }}
                />
                <CardContent>
                  <form onSubmit={handleBuildingSubmit(onEditSubmitBuilding)}>
                    <Grid container spacing={3}>
                      {/* Building Name */}
                      <Grid item xs={6}>
                        <Controller
                          name="name"
                          control={controlEdit}
                          rules={{ required: 'Building name is required' }}
                          render={({ field: { onChange, value } }) => (
                            <MDInput
                              value={value || ''}
                              label="Building Name"
                              placeholder="Enter building name"
                              onChange={onChange}
                              fullWidth
                              required
                              error={!!errorsEdit.name}
                              helperText={errorsEdit.name?.message}
                            />
                          )}
                        />
                      </Grid>

                      {/* Country */}
                      <Grid item xs={6}>
                        <Controller
                          name="country"
                          control={controlEdit}
                          render={({ field: { onChange, value } }) => (
                            <MDInput
                              value={value || ''}
                              label="Country"
                              placeholder="Enter country"
                              onChange={onChange}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>

                      {/* City */}
                      <Grid item xs={6}>
                        <Controller
                          name="city"
                          control={controlEdit}
                          render={({ field: { onChange, value } }) => (
                            <MDInput value={value || ''} label="City" placeholder="Enter city" onChange={onChange} fullWidth />
                          )}
                        />
                      </Grid>

                      {/* Street */}
                      <Grid item xs={6}>
                        <Controller
                          name="street"
                          control={controlEdit}
                          render={({ field: { onChange, value } }) => (
                            <MDInput
                              value={value || ''}
                              label="Street"
                              placeholder="Enter street address"
                              onChange={onChange}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      {/* Street Number*/}
                      {/* <Grid item xs={6}>
                      <Controller
                        name="street_number"
                        control={controlEdit}
                        render={({ field: { onChange, value } }) => (
                          <MDInput
                            value={value || ''}
                            label="Street Number"
                            placeholder="Enter street address"
                            onChange={onChange}
                            fullWidth
                          />
                        )}
                      />
                    </Grid> */}
                      {/* Zip Code */}
                      <Grid item xs={6}>
                        <Controller
                          name="zipCode"
                          control={controlEdit}
                          render={({ field: { onChange, value, ref } }) => (
                            <MDInput
                              value={value ?? ''}
                              label="zipCode"
                              placeholder="Enter zip-code"
                              fullWidth
                              // 1) Sanitize any typing/paste to digits only
                              onChange={e => onChange(onlyDigits(e.target.value))}
                              onPaste={e => {
                                e.preventDefault();
                                const text = (e.clipboardData || window.clipboardData).getData('text');
                                onChange(onlyDigits(text));
                              }}
                              // 2) Mobile keypad + soft validation
                              inputMode="numeric"
                              pattern="\d*"
                              // 3) Optional: limit length (e.g., US ZIP 5 or ZIP+4 without dash)
                              inputProps={{ maxLength: 10 }}
                              ref={ref}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                          <MDTypography variant="body2" fontWeight="medium">
                            CAV Weekly Schedule
                          </MDTypography>
                          <Tooltip title="Define CAV start and end times for each weekday. HVAC units will be automatically turned OFF during non-operating hours.">
                            <IconButton size="small" sx={{ color: 'text.secondary' }}>
                              <InfoIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <Controller
                          name="cav_active_24_7"
                          control={controlEdit}
                          render={({ field: { onChange, value } }) => (
                            <FormControlLabel
                              sx={{ mb: 1 }}
                              control={
                                <Checkbox
                                  checked={Boolean(value)}
                                  onChange={e => {
                                    const isActive24Hours = e.target.checked;
                                    onChange(isActive24Hours);
                                    if (isActive24Hours) {
                                      CAV_WEEK_DAYS.forEach(day => {
                                        setValueEdit(`cav_day_enabled_${day.key}`, true);
                                        setValueEdit(`cav_start_time_${day.key}`, '00:00');
                                        setValueEdit(`cav_end_time_${day.key}`, '00:00');
                                      });
                                    }
                                  }}
                                />
                              }
                              label="Active 24/7"
                            />
                          )}
                        />

                        <LocalizationProvider dateAdapter={AdapterLuxon}>
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'grey.300',
                              borderRadius: 2,
                              p: 1.5,
                              backgroundColor: '#fafafa',
                            }}>
                            <Grid container spacing={1} sx={{ mb: 2.5, alignItems: 'center' }}>
                              <Grid item xs={12} lg={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <MDTypography variant="caption" sx={{ fontWeight: 700 }}>
                                  Day
                                </MDTypography>
                              </Grid>
                              <Grid item xs={12} lg={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <MDTypography variant="caption" sx={{ fontWeight: 700 }}>
                                  Active
                                </MDTypography>
                              </Grid>
                              <Grid item xs={6} lg={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <MDTypography variant="caption" sx={{ fontWeight: 700 }}>
                                  Start Time
                                </MDTypography>
                              </Grid>
                              <Grid item xs={6} lg={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <MDTypography variant="caption" sx={{ fontWeight: 700 }}>
                                  End Time
                                </MDTypography>
                              </Grid>
                            </Grid>

                            {CAV_WEEK_DAYS.map(day =>
                              (() => {
                                const dayIsEnabled = Boolean(buildingEditVals?.[`cav_day_enabled_${day.key}`]);
                                return (
                                  <Grid
                                    container
                                    spacing={1}
                                    key={day.key}
                                    sx={{
                                      py: 0.5,
                                      alignItems: 'center',
                                      borderTop: '1px solid',
                                      borderColor: 'grey.200',
                                    }}>
                                    <Grid item xs={12} lg={3}>
                                      <MDTypography sx={{ fontWeight: '600' }} variant="body2">
                                        {day.label}
                                      </MDTypography>
                                    </Grid>

                                    <Grid item xs={12} lg={3} sx={{ display: 'flex', justifyContent: { lg: 'center' } }}>
                                      <Controller
                                        name={`cav_day_enabled_${day.key}`}
                                        control={controlEdit}
                                        render={({ field: { onChange, value } }) => (
                                          <Toggle
                                            checked={Boolean(value)}
                                            disabled={Boolean(buildingEditVals?.cav_active_24_7)}
                                            handleChange={e => {
                                              const isEnabled = e.target.checked;
                                              onChange(isEnabled);
                                              if (!isEnabled) return;

                                              const startField = `cav_start_time_${day.key}`;
                                              const endField = `cav_end_time_${day.key}`;
                                              if (!buildingEditVals?.[startField]) {
                                                setValueEdit(startField, DEFAULT_CAV_START_TIME);
                                              }
                                              if (!buildingEditVals?.[endField]) {
                                                setValueEdit(endField, DEFAULT_CAV_END_TIME);
                                              }
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>

                                    <Grid item xs={6} lg={3}>
                                      <Controller
                                        name={`cav_start_time_${day.key}`}
                                        control={controlEdit}
                                        render={({ field: { onChange, value } }) => (
                                          <TimePicker
                                            value={toLuxonTime(value)}
                                            onChange={newValue => onChange(newValue ? newValue.toFormat('HH:mm') : '')}
                                            ampm={false}
                                            views={['hours', 'minutes']}
                                            format="HH:mm"
                                            disabled={!dayIsEnabled || Boolean(buildingEditVals?.cav_active_24_7)}
                                            slotProps={{
                                              textField: {
                                                fullWidth: true,
                                                placeholder: 'HH:mm',
                                              },
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>

                                    <Grid item xs={6} lg={3}>
                                      <Controller
                                        name={`cav_end_time_${day.key}`}
                                        control={controlEdit}
                                        render={({ field: { onChange, value } }) => (
                                          <TimePicker
                                            value={toLuxonTime(value)}
                                            onChange={newValue => onChange(newValue ? newValue.toFormat('HH:mm') : '')}
                                            ampm={false}
                                            views={['hours', 'minutes']}
                                            format="HH:mm"
                                            disabled={!dayIsEnabled || Boolean(buildingEditVals?.cav_active_24_7)}
                                            slotProps={{
                                              textField: {
                                                fullWidth: true,
                                                placeholder: 'HH:mm',
                                              },
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>
                                  </Grid>
                                );
                              })(),
                            )}
                          </Box>
                        </LocalizationProvider>
                      </Grid>

                      {/* Submit Buttons */}
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={2}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading || (!isAddMode && !isBuildingDirty)}>
                            {isAddMode ? 'Add Building' : 'Update Building'}
                          </Button>
                          <Button variant="outlined" onClick={handleReset} sx={{ borderColor: 'blue', color: 'blue' }}>
                            Reset
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                    <Grid></Grid>
                  </form>
                </CardContent>
              </Card>
            </Grid>

            {/* MANAGE FLOORS & AREAS SECTION */}
            {!isAddMode && (selectedBuilding?.floors || selectedBuilding?.areas) && (
              <Grid item xs={12}>
                <Card>
                  <CardHeader title="Manage Floors and Areas" sx={{ bgcolor: 'white', color: 'white' }} />
                  <CardContent>
                    {floorsList.map(floor => {
                      const floorAreas = getAreasForFloor(floor.floor);
                      const isExpanded = Boolean(expandedFloors[floor.id]);

                      return (
                        <Box key={floor.id} sx={{ mb: 2 }}>
                          <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                            {/* Floor Number */}
                            <Grid item xs={2}>
                              <Controller
                                name={`floor_${floor.id}`}
                                control={controlFloor}
                                rules={{ required: 'Floor number is required' }}
                                render={({ field: { value } }) => (
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Floor Number
                                    </Typography>
                                    <Typography sx={{ fontWeight: '500' }}>
                                      {value !== null && value !== undefined ? value : '-'}
                                    </Typography>
                                  </Box>
                                )}
                              />
                            </Grid>

                            {/* Floor Size */}
                            <Grid item xs={2.5}>
                              <Controller
                                name={`floor_size_${floor.id}`}
                                control={controlFloor}
                                render={({ field: { onChange, value } }) => (
                                  <MDInput
                                    value={value || ''}
                                    label="Floor Size"
                                    placeholder="Floor Size"
                                    onChange={onChange}
                                    fullWidth
                                    type="number"
                                  />
                                )}
                              />
                            </Grid>

                            {/* actions buttons */}
                            <Grid item xs={7.5} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              <Button
                                startIcon={
                                  floor.scheme !== null && floor.scheme ? (
                                    <EditIcon sx={{ scale: 1.5 }} />
                                  ) : (
                                    <FileUploadIcon sx={{ scale: 1.5 }} />
                                  )
                                }
                                variant="outlined"
                                component="label"
                                size="small"
                                sx={{ color: 'blue' }}>
                                {floor.scheme !== null && floor.scheme ? 'CHANGE IMAGE' : 'UPLOAD IMAGE'}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={e => {
                                    if (e.target.files[0]) {
                                      setSelectedFiles(prev => ({
                                        ...prev,
                                        [floor.floor]: e.target.files[0],
                                      }));
                                    }
                                  }}
                                />
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={!isFloorDirty(floor)}
                                onClick={() => {
                                  handleFloorUpdate(floor, selectedFiles[floor.floor]);
                                }}>
                                {uploading[floor.floor] ? 'Updating...' : 'Update'}
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleDeleteFloor(floor)}
                                sx={{
                                  color: '#ff0000',
                                  border: `1px solid red`,
                                  '&:hover': {
                                    variant: 'contained',
                                    background: 'red',
                                    color: 'white !important',
                                  },
                                }}>
                                Delete
                              </Button>
                              <Button
                                variant="inherit"
                                startIcon={
                                  isExpanded ? <ExpandLessIcon sx={{ scale: '2' }} /> : <ExpandMoreIcon sx={{ scale: '2' }} />
                                }
                                onClick={() =>
                                  setExpandedFloors(prev => ({
                                    ...prev,
                                    [floor.id]: !prev[floor.id],
                                  }))
                                }>
                                {isExpanded ? 'Hide' : `Areas`}
                              </Button>
                            </Grid>
                          </Grid>

                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              className="areas-container"
                              sx={{
                                mt: 2,
                                pl: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                              }}>
                              {floorAreas.length === 0 ? (
                                <MDTypography fontSize={14} sx={{ color: '#666' }}>
                                  No areas configured in this floor.
                                </MDTypography>
                              ) : (
                                floorAreas.map(area => (
                                  <Grid
                                    className="single-area-row"
                                    container
                                    spacing={1.5}
                                    key={area.id}
                                    sx={{
                                      alignItems: 'center',
                                      bgcolor: '#ffffff',
                                      border: '1px solid #d9deeb',
                                      borderRadius: 2,
                                      boxShadow: '0 2px 8px rgba(31, 45, 61, 0.08)',
                                      p: 1.5,
                                      '&:hover': {
                                        borderColor: '#9aa9d6',
                                        boxShadow: '0 3px 10px rgba(31, 45, 61, 0.12)',
                                      },
                                    }}>
                                    {/* Area Name */}
                                    <Grid item xs={6} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Controller
                                        name={`name_${area.id}`}
                                        control={controlArea}
                                        rules={{ required: 'Area name is required' }}
                                        render={({ field: { onChange, value } }) => (
                                          <MDInput
                                            value={value || ''}
                                            label="Area Name"
                                            placeholder="Area Name"
                                            onChange={onChange}
                                            fullWidth
                                            error={!!errorsArea[`name_${area.id}`]}
                                            helperText={errorsArea[`name_${area.id}`]?.message}
                                          />
                                        )}
                                      />
                                    </Grid>

                                    {/* Floor Selection */}
                                    <Grid item xs={6} sm={2.5} className="floorSelect" sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Controller
                                        name={`floor_${area.id}`}
                                        control={controlArea}
                                        render={({ field: { onChange, value } }) => (
                                          <FloorSelect id={`floor-${area.id}`} value={value} onChange={onChange} floors={floorsList} />
                                        )}
                                      />
                                    </Grid>

                                    {/* Area Size */}
                                    <Grid item xs={6} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Controller
                                        name={`area_size_${area.id}`}
                                        control={controlArea}
                                        render={({ field: { onChange, value } }) => (
                                          <MDInput
                                            value={value || ''}
                                            label="Area Size"
                                            placeholder="Area Size"
                                            onChange={onChange}
                                            fullWidth
                                            type="number"
                                          />
                                        )}
                                      />
                                    </Grid>

                                    {/* Choose devices before pressing Update. */}
                                    <Grid item xs={6} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                      <AreaDevicesSelector
                                        area={area}
                                        buildingId={selectedBuilding.id}
                                        buildingName={selectedBuilding.name}
                                        allDevices={allDevices}
                                        selectedDeviceIds={areaDeviceIds[area.id]}
                                        setSelectedDeviceIds={selectedDeviceIds =>
                                          setAreaDeviceIds(previous => ({ ...previous, [area.id]: selectedDeviceIds }))
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                                      <Box sx={{ display: 'flex', gap: 1, width: '25%', maxWidth: '100%' }}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          fullWidth
                                          disabled={updatingAreaId !== null || (!isAreaDirty(area) && !isAreaDevicesDirty(area))}
                                          onClick={() => handleAreaUpdate(area)}>
                                          {updatingAreaId === area.id ? <CircularProgress size={16} color="inherit" /> : 'Update'}
                                        </Button>
                                        <Button
                                          size="small"
                                          fullWidth
                                          disabled={updatingAreaId !== null}
                                          onClick={() => handleDeleteArea(area)}
                                          sx={{
                                            color: '#ff0000',
                                            border: `1px solid red`,
                                            '&:hover': {
                                              variant: 'contained',
                                              background: 'red',
                                              color: 'white !important',
                                            },
                                          }}>
                                          Delete
                                        </Button>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                ))
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}

                    {/* ------------- ADD NEW FLOOR ROW ------------- */}
                    {addFloorMode && (
                      <Grid
                        container
                        sx={{
                          mb: 1,
                          mt: 1,
                          alignItems: 'center',
                          bgcolor: brandColors.FILTER_BAR,
                          p: '1rem',
                          borderRadius: '.5rem',
                          justifyContent: 'space-between',
                        }}>
                        <Grid item xs={6} container spacing={2}>
                          {/* Floor Number */}
                          <Grid item xs={5}>
                            <Controller
                              name="new_floor"
                              control={controlFloor}
                              rules={{ required: 'Floor number is required' }}
                              render={({ field: { onChange, value } }) => (
                                <MDInput
                                  value={value || ''}
                                  label="*Floor Number"
                                  placeholder="Floor Number"
                                  onChange={onChange}
                                  fullWidth
                                  type="number"
                                  error={!!errorsFloor['new_floor']}
                                  helperText={errorsFloor['new_floor']?.message}
                                />
                              )}
                            />
                          </Grid>

                          {/* Floor Size */}
                          <Grid item xs={5}>
                            <Controller
                              name="new_floor_size"
                              control={controlFloor}
                              render={({ field: { onChange, value } }) => (
                                <MDInput
                                  value={value || ''}
                                  label="Floor Size"
                                  placeholder="Floor Size"
                                  onChange={onChange}
                                  fullWidth
                                  type="number"
                                />
                              )}
                            />
                          </Grid>
                        </Grid>

                        {/* actions buttons */}
                        <Grid item container xs={6} sx={{ justifyContent: 'flex-end', pr: 2 }}>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            startIcon={<FileUploadIcon sx={{ scale: 1.5 }} />}
                            sx={{ mr: 0.8, color: 'blue' }}>
                            Upload Image
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={e => {
                                if (e.target.files[0]) {
                                  setSelectedFiles(prev => ({
                                    ...prev,
                                    [0]: e.target.files[0],
                                  }));
                                }
                              }}
                            />
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'blue', color: 'blue' }}
                            disabled={isNewFloorMissing}
                            onClick={() => {
                              const formData = watchFloor();
                              const floorData = {
                                id: 0,
                                floor: formData.new_floor,
                                area_size: formData.new_area_size,
                              };
                              handleFloorUpdate(floorData, selectedFiles[0]);
                            }}>
                            Submit
                          </Button>
                          <Button
                            size="small"
                            sx={{ marginLeft: '.8rem', paddingX: '1px !important' }}
                            onClick={() => {
                              setAddFloorMode(false);
                              resetFloor();
                            }}>
                            Cancel
                          </Button>
                        </Grid>
                      </Grid>
                    )}

                    <Button onClick={() => setAddFloorMode(true)} disabled={addFloorMode}>
                      + Add New Floor
                    </Button>

                    {/* ADD NEW AREA ROW */}
                    {addAreaMode && (
                      <Grid
                        container
                        gap={2}
                        sx={{
                          mb: 1,
                          mt: 2,
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          bgcolor: brandColors.FILTER_BAR,
                          p: '1rem',
                          borderRadius: '.5rem',
                          display: 'flex',
                        }}>
                        <Grid item xs={12} container spacing={1} alignItems="center">
                          {/* Area Name */}
                          <Grid item xs={6} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Controller
                              name="new_name"
                              control={controlArea}
                              rules={{ required: 'Area name is required' }}
                              render={({ field }) => (
                                <MDInput
                                  {...field}
                                  label="Area Name"
                                  placeholder="Type area name"
                                  fullWidth
                                  error={!!errorsArea?.new_name}
                                  helperText={errorsArea?.new_name?.message}
                                />
                              )}
                            />
                          </Grid>
                          {/* Floor Selection */}
                          <Grid item xs={6} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Controller
                              name="new_floor"
                              control={controlArea}
                              className="DropdxownMenuDropdownMenu"
                              defaultValue=""
                              render={({ field: { onChange, value } }) => (
                                <FloorSelect id="new-area-floor" value={value} onChange={onChange} floors={floorsList} />
                              )}
                            />
                          </Grid>
                          {/* Area Size */}
                          <Grid item xs={6} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Controller
                              name="new_area_size"
                              control={controlArea}
                              render={({ field: { onChange, value } }) => (
                                <MDInput
                                  value={value || ''}
                                  label="Area Size"
                                  placeholder="Area Size"
                                  onChange={onChange}
                                  fullWidth
                                  type="number"
                                />
                              )}
                            />
                          </Grid>
                          {/* Choose devices before creating the new area. */}
                          <Grid item xs={6} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
                            <DevicesSelect
                              id="new-area"
                              devices={newAreaDevices}
                              selectedDeviceIds={newAreaDeviceIds}
                              setSelectedDeviceIds={setNewAreaDeviceIds}
                              onClose={() => {}}
                              buildingName={selectedBuilding?.name}
                            />
                            {newAreaDevices.length === 0 && (
                              <Typography variant="caption">No devices are available in this building.</Typography>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 1, width: '25%', maxWidth: '100%' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              fullWidth
                              disabled={updatingAreaId !== null || isNewAreaMissing}
                              sx={{ borderColor: 'blue', color: 'blue' }}
                              onClick={() => {
                                const formData = watchArea();
                                const areaData = {
                                  id: 0,
                                  name: formData.new_name,
                                  floor: Number(formData.new_floor),
                                  area_size: formData.new_area_size,
                                };
                                handleAreaUpdate(areaData);
                              }}>
                              {updatingAreaId === 0 ? <CircularProgress size={16} color="inherit" /> : 'Submit'}
                            </Button>
                            <Button
                              size="small"
                              fullWidth
                              disabled={updatingAreaId !== null}
                              onClick={() => {
                                setAddAreaMode(false);
                                resetArea();
                                setNewAreaDeviceIds([]);
                              }}>
                              Cancel
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    )}

                    <Button
                      onClick={() => {
                        setNewAreaDeviceIds([]);
                        setAddAreaMode(true);
                      }}
                      disabled={addAreaMode}>
                      + Add New Area
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* HVAC EQUIPMENT SECTION */}
            {selectedBuilding && !isAddMode && currentUser.role !== 'c' && (
              <Grid item xs={12}>
                <HvacEquipmentSection buildingId={buildingId} />
              </Grid>
            )}

            {/* DELETE BUILDING SECTION */}
            {selectedBuilding && !isAddMode && currentUser.role !== 'c' && (
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title="Delete Building"
                    sx={{
                      bgcolor: 'error.main',
                      '& .MuiCardHeader-title': { color: '#ffffff' },
                    }}
                  />
                  <CardContent>
                    <Typography variant="body1" gutterBottom>
                      Permanently delete this building from the system. This action cannot be undone.
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleDeleteClick}
                      disabled={loading}
                      sx={{
                        mt: 2,
                        background: '#fff',
                        border: `1px solid #ff0000`,
                        color: '#ff0000',
                        '&:hover': {
                          background: '#ff0000',
                          color: '#fff',
                        },
                      }}>
                      Delete Building
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Back Button */}
            <Grid item xs={12}>
              <Button variant="text" onClick={() => navigate('/manage-buildings')} sx={{ mt: 2 }}>
                ← Back to Buildings List
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialogSlide
        open={deleteModalOpen}
        title="Confirm Building Deletion"
        content={
          <Box sx={{ padding: '8px', fontWeight: 'bold', width: '500px' }}>
            Are you sure you want to delete building "{selectedBuilding?.name}"?
            <br />
            <br />
            This action will cause a chain reaction:
            <br />
            → delete all floors data
            <br />→ delete all areas devision in floor s
            <br />→ all devices changed to unassigned
            <br />
            <br />
            This action cannot be undone.
          </Box>
        }
        handleClose={() => setDeleteModalOpen(false)}
        handleOk={handleDeleteConfirm}
      />
      {/* Delete Floor Confirmation Modal */}
      <AlertDialogSlide
        open={deleteFloorModalOpen}
        title="Confirm Floor Deletion"
        content={
          <Box sx={{ padding: '8px', fontWeight: 'bold', width: '500px' }}>
            Are you sure you want to delete floor {deletingFloor?.floor}?
            <br />
            <br />
            This action will cause a chain reaction:
            <br />
            → delete floor data
            <br />→ delete areas devision in floor
            <br />→ all devices changed to unassigned
            <br />
            <br />
            This action cannot be undone.
          </Box>
        }
        handleClose={() => {
          setDeleteFloorModalOpen(false);
          setDeletingFloor(null);
        }}
        handleOk={handleDeleteFloorConfirm}
      />

      {/* Delete Area Confirmation Modal */}
      <AlertDialogSlide
        open={deleteAreaModalOpen}
        title="Confirm Area Deletion"
        content={
          <Box sx={{ padding: '8px', fontWeight: 'bold', width: '500px' }}>
            Are you sure you want to delete area "{deletingArea?.name}"?
            <br />
            <br />
            This will cause a chain reaction:
            <br />→ delete area data.
            <br />→ all devices changed to unassigned.
            <br />
            <br />
            This action cannot be undone.
          </Box>
        }
        handleClose={() => {
          setDeleteAreaModalOpen(false);
          setDeletingArea(null);
        }}
        handleOk={handleDeleteAreaConfirm}
      />

      {/* Notification Snackbar */}
      <CustomSnackbar
        open={notification.open}
        duration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        vertical="top"
        horizontal="left"
        severity={notification.severity}
        message={notification.message}
        color="#fff"
      />
    </DashboardLayout>
  );
};

export default BuildingForm;
